import { useState, useEffect } from 'react'
import { ChevronDown } from 'lucide-react'
import type { Patient } from '../../store/patientDetailSlice'

interface PatientDetailHeaderProps {
  patient: Patient
  onUpdate: (data: Partial<Patient>) => Promise<void>
  activeTab: string
  onTabChange: (tab: string) => void
}

const SPECIES_OPTIONS = ['Dog', 'Cat', 'Bird', 'Reptile', 'Horse', 'Other']
const SEX_OPTIONS = ['Male', 'Female', 'Unknown']
const TABS = ['Overview', 'Treatment History', 'Prescription History', 'Lab Results']

function getStatusStyle(status: string): string {
  switch (status) {
    case 'Active':
      return 'bg-accent-subtle-bg text-status-success'
    case 'Inactive':
      return 'bg-border text-text-muted'
    case 'Deceased':
      return 'bg-[#2a2826] text-white'
    default:
      return 'bg-border text-text-muted'
  }
}

function computeAge(dob: string | null): string {
  if (!dob) return ''
  const dateStr = dob.split('T')[0]
  const birth = new Date(dateStr + 'T00:00:00')
  const now = new Date()
  let years = now.getFullYear() - birth.getFullYear()
  const monthDiff = now.getMonth() - birth.getMonth()
  if (monthDiff < 0 || (monthDiff === 0 && now.getDate() < birth.getDate())) {
    years--
  }
  if (years < 1) {
    const months = (now.getFullYear() - birth.getFullYear()) * 12 + now.getMonth() - birth.getMonth()
    return months <= 1 ? '1 month' : `${months} months`
  }
  return years === 1 ? '1 year' : `${years} years`
}

export function PatientDetailHeader({ patient, onUpdate, activeTab, onTabChange }: PatientDetailHeaderProps) {
  const [editOpen, setEditOpen] = useState(false)

  const [editName, setEditName] = useState(patient.name)
  const [editSpecies, setEditSpecies] = useState(patient.species)
  const [editBreed, setEditBreed] = useState(patient.breed)
  const [editDob, setEditDob] = useState(patient.date_of_birth || '')
  const [editWeight, setEditWeight] = useState(patient.weight?.toString() || '')
  const [editWeightUnit, setEditWeightUnit] = useState(patient.weight_unit)
  const [editSex, setEditSex] = useState(patient.sex)
  const [showSpeciesDropdown, setShowSpeciesDropdown] = useState(false)
  const [showSexDropdown, setShowSexDropdown] = useState(false)
  const [editSpayNeuter, setEditSpayNeuter] = useState(patient.spay_neuter_status)
  const [editMicrochip, setEditMicrochip] = useState(patient.microchip_number)
  const [editAllergies, setEditAllergies] = useState(patient.allergies)
  const [editOwnerName, setEditOwnerName] = useState(patient.owner_name)
  const [editOwnerPhone, setEditOwnerPhone] = useState(patient.owner_phone)
  const [editOwnerEmail, setEditOwnerEmail] = useState(patient.owner_email)
  const [editNotes, setEditNotes] = useState(patient.notes)
  const [editErrors, setEditErrors] = useState<Record<string, string>>({})

  useEffect(() => {
    if (editOpen) return
    setEditName(patient.name)
    setEditSpecies(patient.species)
    setEditBreed(patient.breed)
    setEditDob(patient.date_of_birth || '')
    setEditWeight(patient.weight?.toString() || '')
    setEditWeightUnit(patient.weight_unit)
    setEditSex(patient.sex)
    setShowSpeciesDropdown(false)
    setShowSexDropdown(false)
    setEditSpayNeuter(patient.spay_neuter_status)
    setEditMicrochip(patient.microchip_number)
    setEditAllergies(patient.allergies)
    setEditOwnerName(patient.owner_name)
    setEditOwnerPhone(patient.owner_phone)
    setEditOwnerEmail(patient.owner_email)
    setEditNotes(patient.notes)
  }, [patient, editOpen])

  const allergyList = patient.allergies
    ? patient.allergies.split(',').map((a) => a.trim()).filter(Boolean)
    : []

  const age = computeAge(patient.date_of_birth)

  const handleEditSave = async () => {
    const errors: Record<string, string> = {}
    if (!editName.trim()) errors.name = 'Patient Name is required'
    if (!editSpecies) errors.species = 'Species is required'
    if (!editOwnerName.trim()) errors.ownerName = 'Owner Name is required'
    if (Object.keys(errors).length > 0) {
      setEditErrors(errors)
      return
    }
    setEditErrors({})
    await onUpdate({
      name: editName.trim(),
      species: editSpecies,
      breed: editBreed,
      date_of_birth: editDob || null,
      weight: editWeight ? Number(editWeight) : null,
      weight_unit: editWeightUnit,
      sex: editSex,
      spay_neuter_status: editSpayNeuter,
      microchip_number: editMicrochip,
      allergies: editAllergies,
      owner_name: editOwnerName.trim(),
      owner_phone: editOwnerPhone,
      owner_email: editOwnerEmail,
      notes: editNotes,
    })
    setEditOpen(false)
  }

  const closeAllDropdowns = () => {
    setShowSpeciesDropdown(false)
    setShowSexDropdown(false)
  }

  const handleEditCancel = () => {
    setEditOpen(false)
    setEditErrors({})
    closeAllDropdowns()
    setEditName(patient.name)
    setEditSpecies(patient.species)
    setEditBreed(patient.breed)
    setEditDob(patient.date_of_birth || '')
    setEditWeight(patient.weight?.toString() || '')
    setEditWeightUnit(patient.weight_unit)
    setEditSex(patient.sex)
    setEditSpayNeuter(patient.spay_neuter_status)
    setEditMicrochip(patient.microchip_number)
    setEditAllergies(patient.allergies)
    setEditOwnerName(patient.owner_name)
    setEditOwnerPhone(patient.owner_phone)
    setEditOwnerEmail(patient.owner_email)
    setEditNotes(patient.notes)
  }

  const inputClass = 'h-7 px-2 text-[13px] text-text-primary border border-border rounded-[4px] bg-base outline-none focus:border-accent'

  return (
    <div data-testid="patient-detail-header">
      <div className="flex items-start justify-between gap-3 mb-3 flex-wrap">
        <div className="flex flex-col gap-2">
          <div className="flex items-center gap-3">
            <h1
              className="text-[16px] font-semibold text-text-primary"
              data-testid="patient-name"
            >
              {patient.name}
            </h1>
            <span
              data-testid="patient-status-badge"
              className={`inline-flex items-center h-5 px-1.5 rounded-[3px] text-[11px] font-medium ${getStatusStyle(patient.status)}`}
            >
              {patient.status}
            </span>
          </div>
          <div className="flex items-center gap-3 text-[13px] text-text-muted flex-wrap">
            <span data-testid="patient-species">{patient.species}</span>
            {patient.breed && (
              <span data-testid="patient-breed">{patient.breed}</span>
            )}
            {patient.weight != null && (
              <span data-testid="patient-weight">{parseFloat(String(patient.weight))} {patient.weight_unit}</span>
            )}
            {age && (
              <span data-testid="patient-age">{age}</span>
            )}
          </div>
          <div className="flex items-center gap-3 text-[13px] text-text-muted flex-wrap">
            <span data-testid="patient-owner-name">Owner: {patient.owner_name}</span>
            {patient.owner_phone && (
              <span data-testid="patient-owner-phone">{patient.owner_phone}</span>
            )}
            {patient.owner_email && (
              <span data-testid="patient-owner-email">{patient.owner_email}</span>
            )}
          </div>
          {allergyList.length > 0 && (
            <div className="flex items-center gap-1.5 flex-wrap" data-testid="patient-allergies">
              {allergyList.map((allergy) => (
                <span
                  key={allergy}
                  data-testid="patient-allergy-badge"
                  className="inline-flex items-center h-5 px-1.5 rounded-[3px] text-[11px] font-medium bg-[#f0dede] text-status-error"
                >
                  {allergy}
                </span>
              ))}
            </div>
          )}
        </div>
        <button
          data-testid="edit-patient-button"
          onClick={() => setEditOpen(true)}
          className="inline-flex items-center h-7 px-3 rounded-[4px] bg-accent text-white text-[13px] font-normal hover:bg-accent-dark transition-colors duration-100 cursor-pointer"
        >
          Edit Patient
        </button>
      </div>

      <div className="flex items-center gap-0 border-b border-border overflow-x-auto" data-testid="patient-tabs">
        {TABS.map((tab) => (
          <button
            key={tab}
            data-testid={`tab-${tab.toLowerCase().replace(/\s+/g, '-')}`}
            onClick={() => onTabChange(tab)}
            className={`inline-flex items-center h-9 px-3 text-[13px] font-medium border-b-2 transition-colors duration-100 cursor-pointer ${
              activeTab === tab
                ? 'border-accent text-accent'
                : 'border-transparent text-text-muted hover:text-text-primary'
            }`}
          >
            {tab}
          </button>
        ))}
      </div>

      {editOpen && (
        <div className="fixed inset-0 z-50 flex items-center justify-center bg-black/30" data-testid="edit-patient-modal" onClick={handleEditCancel}>
          <div className="bg-surface rounded-[8px] shadow-elevation-2 w-full max-w-lg max-sm:max-w-[calc(100%-24px)] p-5 max-sm:p-4 max-h-[90vh] overflow-y-auto" onClick={(e) => e.stopPropagation()}>
            <h2 className="text-[14px] font-semibold text-text-primary mb-4">Edit Patient</h2>
            <div className="flex flex-col gap-3">
              <label className="flex flex-col gap-1 text-[12px] text-text-muted">
                Patient Name *
                <input
                  data-testid="edit-patient-name"
                  type="text"
                  value={editName}
                  onChange={(e) => setEditName(e.target.value)}
                  className={inputClass}
                />
                {editErrors.name && <span className="text-status-error text-[11px]">{editErrors.name}</span>}
              </label>
              <div className="flex flex-col gap-1 text-[12px] text-text-muted">
                Species *
                <div className="relative">
                  <button
                    type="button"
                    data-testid="edit-patient-species"
                    onClick={() => {
                      closeAllDropdowns()
                      setShowSpeciesDropdown(!showSpeciesDropdown)
                    }}
                    className={`${inputClass} w-full appearance-none cursor-pointer flex items-center justify-between`}
                  >
                    {editSpecies || 'Select species...'}
                    <ChevronDown size={12} className="text-text-muted" />
                  </button>
                  {showSpeciesDropdown && (
                    <div
                      data-testid="edit-patient-species-dropdown"
                      className="absolute z-10 top-full left-0 w-full mt-1 bg-surface border border-border rounded-[4px] max-h-48 overflow-y-auto"
                      style={{ boxShadow: 'var(--shadow-elevation-1)' }}
                    >
                      {SPECIES_OPTIONS.map((s) => (
                        <button
                          key={s}
                          type="button"
                          data-testid={`edit-species-option-${s}`}
                          onClick={() => {
                            setEditSpecies(s)
                            setShowSpeciesDropdown(false)
                          }}
                          className="w-full text-left px-2 py-1.5 text-[13px] text-text-primary hover:bg-hover cursor-pointer"
                        >
                          {s}
                        </button>
                      ))}
                    </div>
                  )}
                </div>
                {editErrors.species && <span className="text-status-error text-[11px]">{editErrors.species}</span>}
              </div>
              <label className="flex flex-col gap-1 text-[12px] text-text-muted">
                Breed
                <input
                  data-testid="edit-patient-breed"
                  type="text"
                  value={editBreed}
                  onChange={(e) => setEditBreed(e.target.value)}
                  className={inputClass}
                />
              </label>
              <label className="flex flex-col gap-1 text-[12px] text-text-muted">
                Date of Birth
                <input
                  data-testid="edit-patient-dob"
                  type="date"
                  value={editDob}
                  onChange={(e) => setEditDob(e.target.value)}
                  className={inputClass}
                />
              </label>
              <div className="flex gap-2">
                <label className="flex flex-col gap-1 text-[12px] text-text-muted flex-1">
                  Weight
                  <input
                    data-testid="edit-patient-weight"
                    type="number"
                    value={editWeight}
                    onChange={(e) => setEditWeight(e.target.value)}
                    className={inputClass}
                  />
                </label>
                <label className="flex flex-col gap-1 text-[12px] text-text-muted">
                  Unit
                  <div className="flex items-center h-7 rounded-[4px] border border-border overflow-hidden">
                    <button
                      type="button"
                      data-testid="edit-patient-weight-lbs"
                      onClick={() => setEditWeightUnit('lbs')}
                      className={`px-2 h-full text-[12px] cursor-pointer ${editWeightUnit === 'lbs' ? 'bg-accent text-white' : 'bg-base text-text-secondary'}`}
                    >
                      lbs
                    </button>
                    <button
                      type="button"
                      data-testid="edit-patient-weight-kg"
                      onClick={() => setEditWeightUnit('kg')}
                      className={`px-2 h-full text-[12px] cursor-pointer ${editWeightUnit === 'kg' ? 'bg-accent text-white' : 'bg-base text-text-secondary'}`}
                    >
                      kg
                    </button>
                  </div>
                </label>
              </div>
              <div className="flex flex-col gap-1 text-[12px] text-text-muted">
                Sex
                <div className="relative">
                  <button
                    type="button"
                    data-testid="edit-patient-sex"
                    onClick={() => {
                      closeAllDropdowns()
                      setShowSexDropdown(!showSexDropdown)
                    }}
                    className={`${inputClass} w-full appearance-none cursor-pointer flex items-center justify-between`}
                  >
                    {editSex || 'Select sex...'}
                    <ChevronDown size={12} className="text-text-muted" />
                  </button>
                  {showSexDropdown && (
                    <div
                      data-testid="edit-patient-sex-dropdown"
                      className="absolute z-10 top-full left-0 w-full mt-1 bg-surface border border-border rounded-[4px] max-h-40 overflow-y-auto"
                      style={{ boxShadow: 'var(--shadow-elevation-1)' }}
                    >
                      {SEX_OPTIONS.map((s) => (
                        <button
                          key={s}
                          type="button"
                          data-testid={`edit-sex-option-${s}`}
                          onClick={() => {
                            setEditSex(s)
                            setShowSexDropdown(false)
                          }}
                          className="w-full text-left px-2 py-1.5 text-[13px] text-text-primary hover:bg-hover cursor-pointer"
                        >
                          {s}
                        </button>
                      ))}
                    </div>
                  )}
                </div>
              </div>
              <label className="flex items-center gap-2 text-[12px] text-text-muted cursor-pointer">
                <input
                  data-testid="edit-patient-spay-neuter"
                  type="checkbox"
                  checked={editSpayNeuter}
                  onChange={(e) => setEditSpayNeuter(e.target.checked)}
                  className="accent-accent"
                />
                Spayed/Neutered
              </label>
              <label className="flex flex-col gap-1 text-[12px] text-text-muted">
                Microchip Number
                <input
                  data-testid="edit-patient-microchip"
                  type="text"
                  value={editMicrochip}
                  onChange={(e) => setEditMicrochip(e.target.value)}
                  className={inputClass}
                />
              </label>
              <label className="flex flex-col gap-1 text-[12px] text-text-muted">
                Allergies
                <textarea
                  data-testid="edit-patient-allergies"
                  value={editAllergies}
                  onChange={(e) => setEditAllergies(e.target.value)}
                  rows={2}
                  className="px-2 py-1 text-[13px] text-text-primary border border-border rounded-[4px] bg-base outline-none focus:border-accent resize-y"
                />
              </label>
              <label className="flex flex-col gap-1 text-[12px] text-text-muted">
                Owner Name *
                <input
                  data-testid="edit-patient-owner-name"
                  type="text"
                  value={editOwnerName}
                  onChange={(e) => setEditOwnerName(e.target.value)}
                  className={inputClass}
                />
                {editErrors.ownerName && <span className="text-status-error text-[11px]">{editErrors.ownerName}</span>}
              </label>
              <label className="flex flex-col gap-1 text-[12px] text-text-muted">
                Owner Phone
                <input
                  data-testid="edit-patient-owner-phone"
                  type="text"
                  value={editOwnerPhone}
                  onChange={(e) => setEditOwnerPhone(e.target.value)}
                  className={inputClass}
                />
              </label>
              <label className="flex flex-col gap-1 text-[12px] text-text-muted">
                Owner Email
                <input
                  data-testid="edit-patient-owner-email"
                  type="text"
                  value={editOwnerEmail}
                  onChange={(e) => setEditOwnerEmail(e.target.value)}
                  className={inputClass}
                />
              </label>
              <label className="flex flex-col gap-1 text-[12px] text-text-muted">
                Notes
                <textarea
                  data-testid="edit-patient-notes"
                  value={editNotes}
                  onChange={(e) => setEditNotes(e.target.value)}
                  rows={3}
                  className="px-2 py-1 text-[13px] text-text-primary border border-border rounded-[4px] bg-base outline-none focus:border-accent resize-y"
                />
              </label>
            </div>
            <div className="flex justify-end gap-2 mt-4">
              <button
                data-testid="edit-patient-cancel-button"
                onClick={handleEditCancel}
                className="inline-flex items-center h-7 px-3 rounded-[4px] border border-border text-[13px] text-text-secondary hover:bg-hover transition-colors duration-100 cursor-pointer"
              >
                Cancel
              </button>
              <button
                data-testid="edit-patient-save-button"
                onClick={handleEditSave}
                className="inline-flex items-center h-7 px-3 rounded-[4px] bg-accent text-white text-[13px] font-normal hover:bg-accent-dark transition-colors duration-100 cursor-pointer"
              >
                Save
              </button>
            </div>
          </div>
        </div>
      )}
    </div>
  )
}
